/**
 * Readiness Bar Wrapper Component
 * Integrates the AI Readiness Bar with the application layout
 * Handles visibility based on user authentication and membership status
 */

import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useGamification } from '../../hooks/useGamification';
import AIReadinessBar from './AIReadinessBar';
import { MobileReadinessWidget } from './MobileReadinessWidget';

interface ReadinessBarWrapperProps {
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  disableCollapse?: boolean;
  variant?: 'full' | 'widget';
  isMobile?: boolean;
}

export const ReadinessBarWrapper: React.FC<ReadinessBarWrapperProps> = ({
  isCollapsed: isCollapsedProp,
  onToggleCollapse,
  disableCollapse = false,
  variant = 'full',
  isMobile = false,
}) => {
  const { user } = useAuth();
  const { progress, loading } = useGamification();
  const [internalCollapsed, setInternalCollapsed] = useState(false);

  const isCollapsed = disableCollapse ? false : isCollapsedProp ?? internalCollapsed;

  const handleToggleCollapse = () => {
    if (disableCollapse) {
      return;
    }

    if (onToggleCollapse) {
      onToggleCollapse();
      return;
    }

    setInternalCollapsed((prev) => !prev);
  };

  // Only show for logged-in and verified users
  if (!user || !user.emailVerified) {
    return null;
  }

  // Show loading state or wait for progress
  if (loading) {
    return null;
  }

  // Use default values if no progress data
  const displayData = progress || {
    totalXP: 0,
    readinessPercentage: 0,
    completedTasks: [],
    nextTask: null,
  };

  if (isMobile) {
    return (
      <MobileReadinessWidget
        totalXP={displayData.totalXP}
        readinessPercentage={displayData.readinessPercentage}
        completedTasks={displayData.completedTasks}
        nextTask={displayData.nextTask}
        isExpanded={!isCollapsed}
        onToggle={handleToggleCollapse}
      />
    );
  }

  return (
    <div className={variant === 'full' ? "w-full" : ""}>
      <AIReadinessBar
        totalXP={displayData.totalXP}
        readinessPercentage={displayData.readinessPercentage}
        completedTasks={displayData.completedTasks}
        nextTask={displayData.nextTask}
        isCollapsed={isCollapsed}
        onToggleCollapse={handleToggleCollapse}
        disableCollapse={disableCollapse}
        variant={variant}
      />
    </div>
  );
};

export default ReadinessBarWrapper;
